// src/components/SafeInput.jsx
//
// Input с правильной обработкой Android IME / composition.
//
// Проблема: React контролируемый input + Android Gboard + кириллица.
// Когда вводишь "Прохор" → последнее слово в "composing state" (подчёркнутое).
// На blur Android иногда отменяет composing вместо коммита, и React видит
// расхождение между state и DOM value, синхронизируясь "назад" — текст теряется.
//
// Решение:
//   1. Слушаем compositionstart / compositionend.
//   2. Во время композиции НЕ обновляем state на каждый input event —
//      только запоминаем что идёт композиция.
//   3. На compositionend форсируем коммит: setState(e.target.value).
//   4. На blur: если есть незакоммиченное значение — коммитим его явно.
//
// API такой же как у обычного input — value, onChange, остальные props
// пробрасываются. Используй вместо <input className="input"> прямо
// в местах где пользователь печатает имена/названия на кириллице.

import { useRef, useEffect } from 'react';

export default function SafeInput({
  value,
  onChange,
  onBlur,
  className = 'input',
  ...rest
}) {
  const ref = useRef(null);
  const composingRef = useRef(false);
  const lastValueRef = useRef(value);

  // Если родитель передал новое value, синхронизируем DOM
  // (но НЕ во время композиции — иначе ломаем IME)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (composingRef.current) return;
    if (el.value !== value) {
      el.value = value || '';
    }
    lastValueRef.current = value;
  }, [value]);

  const commit = (newValue) => {
    lastValueRef.current = newValue;
    if (onChange) {
      // Эмулируем синтетический event
      onChange({ target: { value: newValue } });
    }
  };

  const handleInput = (e) => {
    // Во время композиции не коммитим в state — только запоминаем
    if (composingRef.current) {
      lastValueRef.current = e.target.value;
      return;
    }
    commit(e.target.value);
  };

  const handleCompositionStart = () => {
    composingRef.current = true;
  };

  const handleCompositionEnd = (e) => {
    composingRef.current = false;
    // Принудительно коммитим финальное значение после композиции
    commit(e.target.value);
  };

  const handleBlur = (e) => {
    // Если по какой-то причине composing застрял — снимаем и коммитим
    if (composingRef.current) {
      composingRef.current = false;
      commit(e.target.value);
    } else if (e.target.value !== lastValueRef.current) {
      // На всякий — если значение в DOM расходится с тем что в state
      commit(e.target.value);
    }
    if (onBlur) onBlur(e);
  };

  return (
    <input
      ref={ref}
      className={className}
      defaultValue={value}
      onInput={handleInput}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onBlur={handleBlur}
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      autoComplete="off"
      {...rest}
    />
  );
}
